import React from "react";
import { useForkRef } from "@mui/material";
import { DateTimePickerProps } from "./model";
import { prepareValue } from "./utils";
import { POPPER_MODIFIERS } from "./const";

/**
 * Hook for getting Popper settings
 */
export const usePopperProps = (anchorEl: HTMLDivElement | null, anchorRef: React.RefObject<HTMLDivElement | null>) => {
  return React.useMemo(
    () => ({
      anchorEl: anchorEl || anchorRef.current || undefined,
      placement: 'bottom-start' as const,
      disablePortal: false,
      modifiers: POPPER_MODIFIERS,
    }),
    [anchorEl, anchorRef],
  );
};

/**
 * Hook for managing date value state
 */
export const useDateTimePickerValue = (value: string | Date | undefined, valueDateFormat: DateTimePickerProps['valueDateFormat']) => {
  const [innerValue, setInnerValue] = React.useState<Date | null>(() => prepareValue(value, valueDateFormat));

  React.useEffect(() => {
    setInnerValue(prepareValue(value, valueDateFormat));
  }, [value, valueDateFormat]);

  return [innerValue, setInnerValue] as const;
};


/**
 * Hook for managing anchor element for Popper
 */
export const useDateTimePickerAnchor = (ref: React.Ref<HTMLInputElement>) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

  const handleSelectButtonRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) {
      anchorRef.current = node;
      setAnchorEl(node);
    }
  }, []);

  const fieldRef = useForkRef(handleSelectButtonRef, ref);

  const updateAnchorEl = React.useCallback(() => {
    requestAnimationFrame(() => {
      if (anchorRef.current) {
        setAnchorEl(anchorRef.current);
      }
    });
  }, []);

  return {
    anchorRef,
    anchorEl,
    fieldRef,
    updateAnchorEl,
  };
};